import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Spinner,
  Alert,
  Card,
  Badge,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import {
  employeeKpiViewService,
  kpiAPI,
  projectDropdownService,
  clientService
} from "../services/apiService";
import { useAuth } from "../context/AuthContext.jsx";
import Layout from "../components/Layout.jsx";
import { formatFYWeekLabel } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./EmployeeKpi.css";

// Helper function to convert week code (YYYY-Www) to date range
const getWeekDateRange = (weekCode) => {
  if (!weekCode) return { start: null, end: null };

  const [year, week] = weekCode.split("-W");
  if (!year || !week) return { start: null, end: null };

  // Calculate Monday of the week
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const monday = new Date(simple);
  if (dow <= 4) monday.setDate(simple.getDate() - simple.getDay() + 1);
  else monday.setDate(simple.getDate() + 8 - simple.getDay());

  // Calculate Sunday of the week
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return {
    start: monday,
    end: sunday,
  };
};

// Format date to YYYY-MM-DD for input min/max
const formatDateForInput = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  if (isNaN(date)) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Function to get status dot color
const getStatusDotColor = (status) => {
  if (!status) return "#6c757d";
  const s = status.toLowerCase();
  if (s.includes("draft")) return "#6c757d"; // Grey for draft
  if (
    s.includes("pending") ||
    s.includes("review pending") ||
    s.includes("tl review pending") ||
    s.includes("hod review pending") ||
    s.includes("admin review pending")
  ) {
    return "#ffc107"; // Yellow for pending
  }
  if (s.includes("approved") || s.includes("completed") || s.includes("rating completed")) {
    return "#198754"; // Green for completed
  }
  if (s.includes("rejected")) return "#dc3545"; // Red for rejected
  return "#6c757d"; // Default grey
};

// Function to get badge color based on rating value
const getRatingBadgeClass = (rating) => {
  const score = parseFloat(rating);
  // Render ratings as plain black bold text; no background classes
  return "";
};

const KpiSubmissionDetail = () => {
  const { id: week } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [editedKpis, setEditedKpis] = useState({});
  const [saving, setSaving] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [discarding, setDiscarding] = useState({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [kpiToDiscard, setKpiToDiscard] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  // Week date range for validation
  const weekDateRange = getWeekDateRange(week);
  const minDate = weekDateRange.start ? formatDateForInput(weekDateRange.start) : "";
  const maxDate = weekDateRange.end ? formatDateForInput(weekDateRange.end) : "";

  // Dropdown data states
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState({
    projects: false,
    clients: false
  });

  // Role checks
  const roleStr = (user?.role || "").toLowerCase();
  const isTeamLead = roleStr.includes("team lead") || roleStr.includes("tl");
  const isHod = roleStr.includes("hod") || roleStr.includes("head of department");

  // Fetch dropdown options
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions({ projects: true, clients: true });

      try {
        // Fetch projects from projectDropdownService
        const projectsData = await projectDropdownService.getProjects();
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      } catch (error) {
        console.error("Error fetching projects:", error);
        showNotification("danger", "Failed to load projects");
      } finally {
        setLoadingOptions(prev => ({ ...prev, projects: false }));
      }

      try {
        // Fetch clients from clientService.getClients()
        const clientsResponse = await clientService.getClients();
        // The response has { success, message, data: [...] }
        const clientsData = clientsResponse?.data || [];
        setClients(Array.isArray(clientsData) ? clientsData : []);
      } catch (error) {
        console.error("Error fetching clients:", error);
        showNotification("danger", "Failed to load clients");
      } finally {
        setLoadingOptions(prev => ({ ...prev, clients: false }));
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    employeeKpiViewService
      .fetchKpisByWeek(week)
      .then((response) => {
        const data = response?.data || response || [];
        const weekEntry = Array.isArray(data)
          ? data.find((w) => w.week === week)
          : null;

        if (weekEntry) {
          setWeekData(weekEntry);
          setKpis(weekEntry.entries || []);
        } else if (Array.isArray(data) && data.length > 0) {
          setWeekData(data[0]);
          setKpis(data[0].entries || []);
        } else {
          setWeekData(null);
          setKpis([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load KPI details for this week.");
        setLoading(false);
      });
  }, [week]);

// Updated effect to initialize edited values for draft KPIs with existing project/client selections
useEffect(() => {
  if (kpis.length > 0 && projects.length > 0 && clients.length > 0) {
    const initialEdited = {};
    
    kpis.forEach(kpi => {
      if (isDraft(kpi)) {
        const updates = {};
        
        // Get the project name from the KPI
        const kpiProjectName = kpi.project_name || kpi.projectName || "";
        
        if (kpiProjectName) {
          console.log("Looking for project:", kpiProjectName); // For debugging
          
          // Try multiple matching strategies
          const project = projects.find(p => {
            const projectName = p.project_name || p.name || "";
            // Exact match (case insensitive)
            if (projectName.toLowerCase() === kpiProjectName.toLowerCase()) {
              return true;
            }
            // Partial match (if one contains the other)
            if (projectName.toLowerCase().includes(kpiProjectName.toLowerCase()) || 
                kpiProjectName.toLowerCase().includes(projectName.toLowerCase())) {
              return true;
            }
            return false;
          });
          
          if (project) {
            console.log("Found project:", project); // For debugging
            updates.project = project.project_id || project.id;
          } else {
            console.log("No project found for:", kpiProjectName); // For debugging
          }
        }
        
        // Find client ID from client name
        const kpiClientName = kpi.client_name || "";
        
        if (kpiClientName) {
          console.log("Looking for client:", kpiClientName); // For debugging
          
          const client = clients.find(c => {
            const clientName = c.customer_name || "";
            // Exact match (case insensitive)
            if (clientName.toLowerCase() === kpiClientName.toLowerCase()) {
              return true;
            }
            // Partial match
            if (clientName.toLowerCase().includes(kpiClientName.toLowerCase()) ||
                kpiClientName.toLowerCase().includes(clientName.toLowerCase())) {
              return true;
            }
            return false;
          });
          
          if (client) {
            console.log("Found client:", client); // For debugging
            updates.client = client.customer_id;
          } else {
            console.log("No client found for:", kpiClientName); // For debugging
          }
        }
        
        // Preserve other existing values
        if (kpi.description) updates.description = kpi.description;
        if (kpi.tat) updates.tat = kpi.tat;
        if (kpi.achieved_date) updates.achieved_date = kpi.achieved_date;
        if (kpi.remarks) updates.remarks = kpi.remarks;
        
        if (Object.keys(updates).length > 0) {
          initialEdited[kpi.id] = updates;
        }
      }
    });
    
    if (Object.keys(initialEdited).length > 0) {
      setEditedKpis(initialEdited);
    }
  }
}, [kpis, projects, clients]);

  // Check if a KPI is in draft status
  const isDraft = (kpi) => {
    const status = (kpi?.status || "").toLowerCase();
    return status.includes("draft");
  };

  // Get the current value for a draft field (edited or original)
  const getFieldValue = (kpi, field) => {
    if (editedKpis[kpi.id] && editedKpis[kpi.id][field] !== undefined) {
      return editedKpis[kpi.id][field];
    }
    return kpi[field] || "";
  };

  // Handle field change for draft KPIs
  const handleFieldChange = (kpiId, field, value) => {
    setEditedKpis((prev) => ({
      ...prev,
      [kpiId]: {
        ...prev[kpiId],
        [field]: value,
      },
    }));
  };

  // Direct click handler for date inputs
  const handleDateInputClick = (e) => {
    e.stopPropagation();
    const input = e.currentTarget
      .closest(".input-group, .date-picker-wrapper")
      ?.querySelector('input[type="date"]');
    if (input) {
      if (input.showPicker) {
        input.showPicker();
      } else {
        input.focus();
      }
    }
  };

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(
      () => setNotification({ show: false, type: "", message: "" }),
      5000,
    );
  };

  const hideNotification = () => {
    setNotification({ show: false, type: "", message: "" });
  };

  // Save draft
  const handleSaveDraft = async (kpi) => {
    setSaving((prev) => ({ ...prev, [kpi.id]: true }));
    try {
      const updates = editedKpis[kpi.id] || {};

      // Get project name from selected project ID
      let projectName = "";
      if (updates.project) {
        const selectedProject = projects.find(p => String(p.project_id) === String(updates.project) || String(p.id) === String(updates.project));
        projectName = selectedProject?.project_name || selectedProject?.name || "";
      } else {
        projectName = kpi.project_name || kpi.projectName || "";
      }

      // Get client name from selected client ID
      let clientName = "";
      if (updates.client) {
        const selectedClient = clients.find(c => String(c.customer_id) === String(updates.client));
        clientName = selectedClient?.customer_name || "";
      } else {
        clientName = kpi.client_name || "";
      }

      const payload = {
        project: updates.project ?? kpi.project,
        projectName: projectName,
        client: updates.client ?? kpi.client,
        clientName: clientName,
        description: updates.description ?? kpi.description,
        tat: updates.tat ?? kpi.tat,
        achieved_date: updates.achieved_date ?? kpi.achieved_date,
        remarks: updates.remarks ?? kpi.remarks,
      };

      await kpiAPI.updateDraft(kpi.id, payload);

      // Update local state with saved values
      setKpis((prev) =>
        prev.map((k) =>
          k.id === kpi.id
            ? {
              ...k,
              ...payload,
              project_name: projectName,
              project: updates.project ?? kpi.project,
              client: updates.client ?? kpi.client,
              client_name: clientName
            }
            : k,
        ),
      );
      setEditedKpis((prev) => {
        const newState = { ...prev };
        delete newState[kpi.id];
        return newState;
      });
      showNotification("success", "Draft saved successfully.");
    } catch (err) {
      console.error("Error saving draft:", err);
      showNotification("danger", "Failed to save draft.");
    } finally {
      setSaving((prev) => ({ ...prev, [kpi.id]: false }));
    }
  };

  // Submit draft with validation (remarks optional now)
  const handleSubmitDraft = async (kpi) => {
    // Gather current values (edited or original)
    const updates = editedKpis[kpi.id] || {};

    // Get project name from selected project ID
    let projectName = "";
    if (updates.project) {
      const selectedProject = projects.find(p => String(p.project_id) === String(updates.project) || String(p.id) === String(updates.project));
      projectName = selectedProject?.project_name || selectedProject?.name || "";
    } else {
      projectName = kpi.project_name || kpi.projectName || "";
    }

    // Get client name from selected client ID
    let clientName = "";
    if (updates.client) {
      const selectedClient = clients.find(c => String(c.customer_id) === String(updates.client));
      clientName = selectedClient?.customer_name || "";
    } else {
      clientName = kpi.client_name || "";
    }

    const project = updates.project ?? kpi.project ?? "";
    const client = updates.client ?? kpi.client ?? "";
    const description = updates.description ?? kpi.description ?? "";
    const tat = updates.tat ?? kpi.tat ?? "";
    const achieved_date = updates.achieved_date ?? kpi.achieved_date ?? "";
    const remarks = updates.remarks ?? kpi.remarks ?? "";

    // Validation: all fields required except remarks
    if (
      !project ||
      !client ||
      !description.trim() ||
      !tat ||
      !achieved_date
    ) {
      showNotification(
        "danger",
        "Please fill in all required fields before submitting the draft.",
      );
      return;
    }

    setSubmitting((prev) => ({ ...prev, [kpi.id]: true }));
    try {
      // Send all updated fields in the request body
      await kpiAPI.submitDraft(kpi.id, {
        project,
        projectName,
        client,
        clientName,
        description,
        tat,
        achieved_date,
        remarks,
      });

      // Update local state to reflect submission
      setKpis((prev) =>
        prev.map((k) =>
          k.id === kpi.id
            ? {
              ...k,
              status: "TL Review Pending",
              project: project,
              project_name: projectName,
              projectName: projectName,
              client: client,
              client_name: clientName,
              description,
              tat,
              achieved_date,
              remarks,
            }
            : k,
        ),
      );
      setEditedKpis((prev) => {
        const newState = { ...prev };
        delete newState[kpi.id];
        return newState;
      });
      showNotification("success", "Draft submitted for review.");

      // Navigate back after successful submission
      setTimeout(() => {
        hideNotification();
        navigate(-1);
      }, 1500);
    } catch (err) {
      console.error("Error submitting draft:", err);
      showNotification("danger", "Failed to submit draft.");
    } finally {
      setSubmitting((prev) => ({ ...prev, [kpi.id]: false }));
    }
  };

  // Handle discard draft
  const handleDiscardDraft = async () => {
    if (!kpiToDiscard) return;

    setDiscarding((prev) => ({ ...prev, [kpiToDiscard.id]: true }));
    try {
      await kpiAPI.deleteDraft(kpiToDiscard.id);
      // Remove the discarded KPI from the list
      setKpis((prev) => prev.filter((k) => k.id !== kpiToDiscard.id));
      // Also remove from editedKpis if it exists
      setEditedKpis((prev) => {
        const newState = { ...prev };
        delete newState[kpiToDiscard.id];
        return newState;
      });
      showNotification("success", "Draft discarded successfully.");
      setShowDiscardModal(false);
      setKpiToDiscard(null);
    } catch (err) {
      console.error("Error discarding draft:", err);
      showNotification("danger", "Failed to discard draft.");
    } finally {
      setDiscarding((prev) => ({ ...prev, [kpiToDiscard.id]: false }));
    }
  };

  // Confirm discard
  const confirmDiscard = (kpi) => {
    setKpiToDiscard(kpi);
    setShowDiscardModal(true);
  };

  // Memoized options
  const clientOptions = clients.map((c) => ({
    value: c.customer_id,
    label: c.customer_name,
  }));

  const projectOptions = projects.map((p) => ({
    value: p.project_id || p.id,
    label: p.project_name || p.name,
  }));

  if (loading)
    return (
      <Layout>
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#76df23" }} />
          <p className="text-muted mt-3">Loading KPI details...</p>
        </div>
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="container mt-4">
          <Alert variant="danger">{error}</Alert>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>
      </Layout>
    );

  if (!weekData || kpis.length === 0)
    return (
      <Layout>
        <div className="container mt-4">
          <Alert variant="warning">No KPI records found for week {week}.</Alert>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <main id="main" className="main employee-kpi-page">
        <div className="container-fluid">
          {/* Back Button */}
          <button
            type="button"
            className="btn btn-outline-secondary mb-3"
            onClick={() => navigate(-1)}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <i className="bi bi-arrow-left"></i> Back
          </button>

          {/* Industry Standard Centered Popup Notification */}
          {notification.show && (
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 9999,
                width: "400px",
                backgroundColor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                border: "none",
                overflow: "hidden",
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              <div
                style={{
                  height: "8px",
                  width: "100%",
                  backgroundColor:
                    notification.type === "success" ? "#10b981" :
                      notification.type === "danger" ? "#ef4444" :
                        notification.type === "warning" ? "#f59e0b" : "#3b82f6",
                }}
              />

              <div style={{ padding: "24px 24px 20px 24px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        notification.type === "success" ? "#ecfdf5" :
                          notification.type === "danger" ? "#fef2f2" :
                            notification.type === "warning" ? "#fffbeb" : "#eff6ff",
                      flexShrink: 0,
                    }}
                  >
                    {notification.type === "success" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {notification.type === "danger" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {notification.type === "warning" && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M5.07183 3H18.9282C20.4678 3 21.4301 4.64845 20.6603 5.98107L13.7321 18.243C12.962 19.5757 11.038 19.5757 10.2679 18.243L3.33975 5.98107C2.56992 4.64845 3.53218 3 5.07183 3Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#111827",
                        lineHeight: "1.4",
                      }}
                    >
                      {notification.type === "success" && "Success"}
                      {notification.type === "danger" && "Error"}
                      {notification.type === "warning" && "Warning"}
                    </h3>
                    <p
                      style={{
                        margin: "0",
                        fontSize: "14px",
                        color: "#6b7280",
                        lineHeight: "1.5",
                      }}
                    >
                      {notification.message}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={hideNotification}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f3f4f6";
                      e.target.style.borderColor = "#d1d5db";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#f9fafb";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: "#76df23", fontSize: "24px" }}>
                KPI Details
              </h2>
              <small className="text-muted" style={{ fontSize: "14px" }}>
                {weekData.week_start && (
                  <>Week starting: {formatDate(weekData.week_start)}</>
                )}
                {" • "}
                {kpis.length} KPI {kpis.length === 1 ? "entry" : "entries"}
              </small>
            </div>
            {week && (
              <Badge
                bg="success"
                text="light"
                className="px-3 py-2"
                style={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  borderRadius: "20px",
                }}
              >
                <i className="bi bi-calendar-week me-2"></i>
                {formatFYWeekLabel(week)} {/* [EDITED: KPI Task - Mahesh Sharma] */}
              </Badge>
            )}
          </div>

          {kpis.map((kpi, index) =>
            isDraft(kpi) ? (
              // Draft: Show in editable Card with EmployeeKpi styling
              <Card key={kpi.id || index} className="shadow-sm border-0 mb-4" style={{ backgroundColor: "#fafafa" }}>
                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                  <div className="d-flex align-items-center">
                    <span className="fw-bold text-primary">
                      KPI #{index + 1}
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: getStatusDotColor(kpi.status),
                        marginRight: "8px"
                      }}
                    ></span>
                    <span style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                      {kpi.status || "-"}
                    </span>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Form>
                    {/* Row 1: Client, Project, Target Date, Achieved Date */}
                    <Row className="g-3 mb-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Client Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={getFieldValue(kpi, "client")}
                            onChange={(e) =>
                              handleFieldChange(kpi.id, "client", e.target.value)
                            }
                            disabled={loadingOptions.clients}
                          >
                            <option value="">Select Client</option>
                            {clientOptions.map(({ value, label }) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </Form.Select>
                          {loadingOptions.clients && (
                            <div className="text-muted small mt-1">
                              <Spinner size="sm" animation="border" /> Loading clients...
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Project Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={getFieldValue(kpi, "project")}
                            onChange={(e) =>
                              handleFieldChange(kpi.id, "project", e.target.value)
                            }
                            disabled={loadingOptions.projects}
                          >
                            <option value="">Select Project</option>
                            {projectOptions.map(({ value, label }) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </Form.Select>
                          {loadingOptions.projects && (
                            <div className="text-muted small mt-1">
                              <Spinner size="sm" animation="border" /> Loading projects...
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Target Date <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="date-picker-wrapper">
                            <Form.Control
                              type="date"
                              value={
                                getFieldValue(kpi, "tat")
                                  ? getFieldValue(kpi, "tat").split("T")[0]
                                  : ""
                              }
                              onChange={(e) =>
                                handleFieldChange(kpi.id, "tat", e.target.value)
                              }
                              onClick={handleDateInputClick}
                              min={minDate}
                              max={maxDate}
                            />
                            <i
                              className="bi bi-calendar3 date-picker-icon"
                              onClick={handleDateInputClick}
                            ></i>
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Achieved Date <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="date-picker-wrapper">
                            <Form.Control
                              type="date"
                              value={
                                getFieldValue(kpi, "achieved_date")
                                  ? getFieldValue(kpi, "achieved_date").split("T")[0]
                                  : ""
                              }
                              onChange={(e) =>
                                handleFieldChange(kpi.id, "achieved_date", e.target.value)
                              }
                              onClick={handleDateInputClick}
                              min={minDate}
                              max={maxDate}
                            />
                            <i
                              className="bi bi-calendar3 date-picker-icon"
                              onClick={handleDateInputClick}
                            ></i>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Row 2: Description and Remarks */}
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Description <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            value={getFieldValue(kpi, "description")}
                            rows={4}
                            onChange={(e) =>
                              handleFieldChange(kpi.id, "description", e.target.value)
                            }
                            placeholder="Describe the KPI in detail..."
                          />
                          <Form.Text className="text-muted">
                            Max 500 characters
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            Remarks
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            value={getFieldValue(kpi, "remarks")}
                            rows={4}
                            onChange={(e) =>
                              handleFieldChange(kpi.id, "remarks", e.target.value)
                            }
                            placeholder="Add any additional remarks (optional)..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <Row className="mt-4">
                      <Col md={12}>
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            variant="outline-danger"
                            onClick={() => confirmDiscard(kpi)}
                            disabled={saving[kpi.id] || submitting[kpi.id] || discarding[kpi.id]}
                          >
                            {discarding[kpi.id] ? (
                              <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Discarding...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-trash me-2"></i>Discard
                              </>
                            )}
                          </Button>

                          <Button
                            variant="outline-secondary"
                            onClick={() => handleSaveDraft(kpi)}
                            disabled={saving[kpi.id] || submitting[kpi.id] || discarding[kpi.id]}
                          >
                            {saving[kpi.id] ? (
                              <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-save me-2"></i>Save Draft
                              </>
                            )}
                          </Button>

                          <Button
                            variant="success"
                            onClick={() => handleSubmitDraft(kpi)}
                            disabled={saving[kpi.id] || submitting[kpi.id] || discarding[kpi.id]}
                            style={{
                              backgroundColor: "#76df23",
                              borderColor: "#76df23",
                            }}
                          >
                            {submitting[kpi.id] ? (
                              <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>Submit for Review
                              </>
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            ) : (
              // Non-draft: Show in Card with plain text and status dot in header
              <Card key={kpi.id || index} className="shadow-sm border-0 mb-3">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center px-4 py-3">
                  <span className="fw-semibold">
                    KPI #{index + 1} -{" "}
                    {kpi.project_name || kpi.projectName || "N/A"}
                  </span>
                  <div className="d-flex align-items-center">
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: getStatusDotColor(kpi.status),
                        marginRight: "8px"
                      }}
                    ></span>
                    <span style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                      {kpi.status || "-"}
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    {/* First line: Client, Project, Target Date, Achieved Date */}
                    <Col md={3}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Client Name
                        </small>
                        <span>{kpi.client_name || kpi.client || "-"}</span>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Project Name
                        </small>
                        <span>{kpi.project_name || kpi.projectName || "-"}</span>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Target Date
                        </small>
                        <span>{formatDate(kpi.tat)}</span>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Achieved Date
                        </small>
                        <span>{formatDate(kpi.achieved_date)}</span>
                      </div>
                    </Col>

                    {/* Second line: Description and Remarks */}
                    <Col md={6}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Description
                        </small>
                        <span>{kpi.description || "-"}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Remarks
                        </small>
                        <span>{kpi.remarks || "-"}</span>
                      </div>
                    </Col>

                    {/* Third line: Points fields (TL, HOD, Admin) - All with same badge styling */}
                    {!isTeamLead && !isHod && (
                      <Col md={4}>
                        <div>
                          <small className="fw-semibold text-secondary d-block mb-1">
                            TL Rating
                          </small>
                          {(() => {
                            const pts = kpi.TLPoints ?? kpi.raw?.TLPoints;
                            const label = kpi.tl_rating;
                            if (pts !== null && pts !== undefined && pts !== "") {
                              return (
                                <span style={{ fontSize: "0.85rem", color: "#000", fontWeight: 700 }}>
                                  {pts}/10{label ? <> &mdash; <span style={{ color: "#2c5f2d" }}>{label}</span></> : null}
                                </span>
                              );
                            }
                            return <span className="text-muted">Rating not given</span>;
                          })()}
                        </div>
                      </Col>
                    )}
                    {!isHod && (
                      <Col md={4}>
                        <div>
                          <small className="fw-semibold text-secondary d-block mb-1">
                            HOD Ratings
                          </small>
                          {typeof kpi.hod_points !== "undefined" &&
                            kpi.hod_points !== null &&
                            kpi.hod_points !== "" ? (
                            <span
                              className={`px-3 py-2`}
                              style={{ fontSize: "0.85rem", color: "#000", fontWeight: 700 }}
                            >
                              {kpi.hod_points}
                            </span>
                          ) : (
                            <span>Rating not given</span>
                          )}
                        </div>
                      </Col>
                    )}
                    <Col md={4}>
                      <div>
                        <small className="fw-semibold text-secondary d-block mb-1">
                          Admin Rating
                        </small>
                          {typeof kpi.mg_points !== "undefined" &&
                          kpi.mg_points !== null &&
                          kpi.mg_points !== "" ? (
                          <span
                            className={`px-3 py-2`}
                            style={{ fontSize: "0.85rem", color: "#000", fontWeight: 700 }}
                          >
                            {kpi.mg_points}
                          </span>
                        ) : (
                          <span>Rating not given</span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ),
          )}

          {/* Discard Confirmation Modal */}
          <Modal show={showDiscardModal} onHide={() => setShowDiscardModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Discard Draft</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to discard this KPI draft?</p>
              <p className="text-muted small mb-0">
                <strong>Project:</strong> {kpiToDiscard?.project_name || kpiToDiscard?.projectName || "N/A"}
              </p>
              <p className="text-danger small mt-2 mb-0">
                <i className="bi bi-exclamation-triangle me-1"></i>
                This action cannot be undone.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDiscardModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDiscardDraft}
                disabled={kpiToDiscard && discarding[kpiToDiscard.id]}
              >
                {kpiToDiscard && discarding[kpiToDiscard.id] ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Discarding...
                  </>
                ) : (
                  'Yes, Discard'
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </main>
    </Layout>
  );
};

export default KpiSubmissionDetail;